import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

/** Mirrors the CHECK on hotel_rooms.space_type. */
export type HotelSpaceType = "bed" | "capacity";

export interface HotelBed {
  id: string;
  name: string;
  sortOrder: number;
  /** Name of the pet sleeping here today, when the bed is taken. */
  occupantName: string | null;
}

export interface HotelRoom {
  id: string;
  name: string;
  color: string | null;
  spaceType: HotelSpaceType;
  capacity: number | null;
  sortOrder: number;
  beds: HotelBed[];
  /** Pets in the room today. For `bed` rooms this equals the taken bed count. */
  occupancy: number;
}

export interface HotelRoomInput {
  name: string;
  color: string | null;
  spaceType: HotelSpaceType;
  capacity: number | null;
}

export const hotelRoomsKey = (clinicId: string | null) => ["hotel-rooms", clinicId];

const today = () => new Date().toLocaleDateString("en-CA");

export function useHotelRooms() {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: hotelRoomsKey(clinicId),
    queryFn: async (): Promise<HotelRoom[]> => {
      if (!clinicId) return [];
      const day = today();

      const [roomsResult, reservationsResult] = await Promise.all([
        supabase
          .from("hotel_rooms")
          .select(
            `id, name, color, space_type, capacity, sort_order,
             hotel_room_beds ( id, name, sort_order, is_active )`,
          )
          .eq("clinic_id", clinicId)
          .eq("is_active", true)
          .order("sort_order"),
        supabase
          .from("stay_reservations")
          .select("hotel_room_id, hotel_room_bed_id, pets ( name )")
          .eq("clinic_id", clinicId)
          .in("status", ["reserved", "checked_in"])
          .lte("check_in_date", day)
          .or(`check_out_date.is.null,check_out_date.gte.${day}`),
      ]);

      if (roomsResult.error) throw roomsResult.error;
      if (reservationsResult.error) throw reservationsResult.error;

      const reservations = (reservationsResult.data ?? []) as Array<{
        hotel_room_id: string | null;
        hotel_room_bed_id: string | null;
        pets: { name: string } | null;
      }>;

      const occupantByBed = new Map<string, string>();
      const countByRoom = new Map<string, number>();
      for (const reservation of reservations) {
        if (reservation.hotel_room_bed_id) {
          occupantByBed.set(reservation.hotel_room_bed_id, reservation.pets?.name ?? "");
        }
        if (reservation.hotel_room_id) {
          countByRoom.set(reservation.hotel_room_id, (countByRoom.get(reservation.hotel_room_id) ?? 0) + 1);
        }
      }

      return (roomsResult.data ?? []).map((room) => {
        const beds = ((room.hotel_room_beds ?? []) as Array<{
          id: string;
          name: string;
          sort_order: number;
          is_active: boolean;
        }>)
          .filter((bed) => bed.is_active)
          .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
          .map((bed) => ({
            id: bed.id,
            name: bed.name,
            sortOrder: bed.sort_order,
            occupantName: occupantByBed.get(bed.id) ?? null,
          }));

        return {
          id: room.id,
          name: room.name,
          color: room.color,
          spaceType: room.space_type as HotelSpaceType,
          capacity: room.capacity,
          sortOrder: room.sort_order,
          beds,
          occupancy:
            room.space_type === "bed"
              ? beds.filter((bed) => bed.occupantName !== null).length
              : countByRoom.get(room.id) ?? 0,
        };
      });
    },
    enabled: !!clinicId,
  });
}

export function useCreateHotelRoom() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: HotelRoomInput) => {
      if (!clinicId) throw new Error("no_clinic_selected");
      const { data, error } = await supabase
        .from("hotel_rooms")
        .insert({
          clinic_id: clinicId,
          name: input.name,
          color: input.color,
          space_type: input.spaceType,
          // hotel_rooms_capacity_chk: capacity is required for `capacity`
          // rooms and must be NULL for `bed` rooms.
          capacity: input.spaceType === "capacity" ? input.capacity : null,
        })
        .select("id")
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelRoomsKey(clinicId) });
    },
  });
}

export function useUpdateHotelRoom() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async ({ id, ...input }: HotelRoomInput & { id: string }) => {
      const { error } = await supabase
        .from("hotel_rooms")
        .update({
          name: input.name,
          color: input.color,
          space_type: input.spaceType,
          capacity: input.spaceType === "capacity" ? input.capacity : null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelRoomsKey(clinicId) });
    },
  });
}

/** Capacity stepper on the room card — narrower than a full room update. */
export function useSaveRoomCapacity() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async ({ id, capacity }: { id: string; capacity: number }) => {
      const { error } = await supabase.from("hotel_rooms").update({ capacity }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelRoomsKey(clinicId) });
    },
  });
}

export function useDeleteHotelRoom() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("hotel_rooms").update({ is_active: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelRoomsKey(clinicId) });
    },
  });
}

export function useCreateHotelBed() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async ({ roomId, name, sortOrder }: { roomId: string; name: string; sortOrder: number }) => {
      const { error } = await supabase
        .from("hotel_room_beds")
        .insert({ hotel_room_id: roomId, name, sort_order: sortOrder });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelRoomsKey(clinicId) });
    },
  });
}

export function useUpdateHotelBed() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase.from("hotel_room_beds").update({ name }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelRoomsKey(clinicId) });
    },
  });
}

export function useDeleteHotelBed() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (id: string) => {
      const { error: reservationDaysError } = await supabase
        .from("stay_reservation_days")
        .update({ hotel_room_bed_id: null })
        .eq("hotel_room_bed_id", id);
      if (reservationDaysError) throw reservationDaysError;

      const { error: reservationsError } = await supabase
        .from("stay_reservations")
        .update({ hotel_room_bed_id: null })
        .eq("hotel_room_bed_id", id);
      if (reservationsError) throw reservationsError;

      const { error } = await supabase.from("hotel_room_beds").update({ is_active: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hotelRoomsKey(clinicId) });
    },
  });
}
